import React, { useState, useEffect } from 'react';
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  InputNumber,
  DatePicker,
  Select,
  message,
  Space,
  Tag,
  Card,
  Statistic,
  Row,
  Col,
  Popconfirm,
  Descriptions,
  Divider,
} from 'antd';
import {
  PlusOutlined,
  ReloadOutlined,
  DeleteOutlined,
  ArrowUpOutlined,
  ArrowDownOutlined,
  EyeOutlined,
} from '@ant-design/icons';
import { apiClient } from '../../../config/apiClient';
import MainLayout from '../../layout/layout';
import { usePermissions } from '../../../context/permissionsContext';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

dayjs.extend(utc);
dayjs.extend(timezone);

// Función helper para parsear timestamps de PostgreSQL
// Sin zona horaria: el backend devuelve hora local (Argentina), mostrar tal cual
const formatFechaConHora = (timestamp) => {
  if (!timestamp) return '-';
  if (typeof timestamp === 'string' && !timestamp.includes('Z') && !timestamp.includes('+') && !timestamp.match(/-\d{2}:\d{2}$/)) {
    return dayjs(timestamp.replace(' ', 'T')).format('DD/MM/YYYY HH:mm');
  }
  return dayjs(timestamp).format('DD/MM/YYYY HH:mm');
};

const { RangePicker } = DatePicker;
const { TextArea } = Input;

const CajaDiaria = () => {
  const { hasPermission } = usePermissions();
  const [movimientos, setMovimientos] = useState([]);
  const [resumenHoy, setResumenHoy] = useState({
    total_ingresos: 0,
    total_egresos: 0,
    saldo_diario: 0,
    cantidad_movimientos: 0
  });
  const [resumenFormasPago, setResumenFormasPago] = useState([]);
  const [resumenGastos, setResumenGastos] = useState([]);
  const [resumenCuentaCorriente, setResumenCuentaCorriente] = useState({ total_deuda: 0, total_pagos: 0, cantidad_deuda: 0, cantidad_pagos: 0 });
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [modalDetalleVisible, setModalDetalleVisible] = useState(false);
  const [comprobanteSeleccionado, setComprobanteSeleccionado] = useState(null);
  const [loadingDetalle, setLoadingDetalle] = useState(false);
  const [form] = Form.useForm();
  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= 768);
  const [fechaRango, setFechaRango] = useState([dayjs(), dayjs()]);

  const loadMovimientos = async (rangoOverride = null) => {
    const rango = rangoOverride != null ? rangoOverride : fechaRango;
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (rango && rango[0]) {
        params.append('fecha_desde', dayjs(rango[0]).format('YYYY-MM-DD'));
      }
      if (rango && rango[1]) {
        params.append('fecha_hasta', dayjs(rango[1]).format('YYYY-MM-DD'));
      }
      
      const response = await apiClient.get(`/gestion/caja-diaria?${params.toString()}`);
      if (response.ok) {
        const data = await response.json();
        setMovimientos(data);
      } else {
        message.error('Error al cargar los movimientos');
      }
    } catch (error) {
      console.error('Error:', error);
      message.error('Error al cargar los movimientos');
    } finally {
      setLoading(false);
    }
  };

  const loadResumenHoy = async (rangoOverride = null) => {
    const rango = rangoOverride != null ? rangoOverride : fechaRango;
    try {
      const params = new URLSearchParams();
      if (rango && rango[0]) {
        params.append('fecha_desde', dayjs(rango[0]).format('YYYY-MM-DD'));
      }
      if (rango && rango[1]) {
        params.append('fecha_hasta', dayjs(rango[1]).format('YYYY-MM-DD'));
      }
      const response = await apiClient.get(`/gestion/caja-diaria/resumen-hoy?${params.toString()}`);
      if (response.ok) {
        const data = await response.json();
        setResumenHoy(data);
      }
    } catch (error) {
      console.error('Error al cargar resumen:', error);
    }
  };

  const loadResumenFormasPago = async (rangoOverride = null) => {
    const rango = rangoOverride != null ? rangoOverride : fechaRango;
    try {
      const params = new URLSearchParams();
      if (rango && rango[0]) {
        params.append('fecha_desde', dayjs(rango[0]).format('YYYY-MM-DD'));
      }
      if (rango && rango[1]) {
        params.append('fecha_hasta', dayjs(rango[1]).format('YYYY-MM-DD'));
      }
      const response = await apiClient.get(`/gestion/caja-diaria/resumen-formas-pago-hoy?${params.toString()}`);
      if (response.ok) {
        const data = await response.json();
        setResumenFormasPago(data || []);
      } else {
        console.error('Error al cargar resumen por formas de pago:', response.status);
        setResumenFormasPago([]);
      }
    } catch (error) {
      console.error('Error al cargar resumen por formas de pago:', error);
      setResumenFormasPago([]);
    }
  };

  const loadResumenGastos = async (rangoOverride = null) => {
    const rango = rangoOverride != null ? rangoOverride : fechaRango;
    try {
      const params = new URLSearchParams();
      if (rango && rango[0]) {
        params.append('fecha_desde', dayjs(rango[0]).format('YYYY-MM-DD'));
      }
      if (rango && rango[1]) {
        params.append('fecha_hasta', dayjs(rango[1]).format('YYYY-MM-DD'));
      }
      const response = await apiClient.get(`/gestion/caja-diaria/resumen-gastos-hoy?${params.toString()}`);
      if (response.ok) {
        const data = await response.json();
        setResumenGastos(data);
      }
    } catch (error) {
      console.error('Error al cargar resumen de gastos:', error);
    }
  };

  const loadResumenCuentaCorriente = async (rangoOverride = null) => {
    const rango = rangoOverride != null ? rangoOverride : fechaRango;
    try {
      const params = new URLSearchParams();
      if (rango && rango[0]) {
        params.append('fecha_desde', dayjs(rango[0]).format('YYYY-MM-DD'));
      }
      if (rango && rango[1]) {
        params.append('fecha_hasta', dayjs(rango[1]).format('YYYY-MM-DD'));
      }
      const response = await apiClient.get(`/gestion/caja-diaria/resumen-cuenta-corriente-hoy?${params.toString()}`);
      if (response.ok) {
        const data = await response.json();
        setResumenCuentaCorriente(data || { total_deuda: 0, total_pagos: 0, cantidad_deuda: 0, cantidad_pagos: 0 });
      }
    } catch (error) {
      console.error('Error al cargar resumen cuenta corriente:', error);
      setResumenCuentaCorriente({ total_deuda: 0, total_pagos: 0, cantidad_deuda: 0, cantidad_pagos: 0 });
    }
  };

  useEffect(() => {
    loadMovimientos();
  }, []);

  // Cargar resúmenes cuando cambia el filtro de fechas
  useEffect(() => {
    if (fechaRango && fechaRango[0] && fechaRango[1]) {
      loadResumenHoy(fechaRango);
      loadResumenFormasPago(fechaRango);
      loadResumenGastos(fechaRango);
      loadResumenCuentaCorriente(fechaRango);
    }
  }, [fechaRango]);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleOpenModal = () => {
    form.resetFields();
    form.setFieldsValue({
      fecha: dayjs(),
      tipo_movimiento: 'ingreso',
      metodo_pago: 'efectivo',
      tipo_referencia: 'ajuste',
    });
    setModalVisible(true);
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      const dataToSend = {
        fecha: values.fecha.format('YYYY-MM-DD'),
        tipo_movimiento: values.tipo_movimiento,
        concepto: values.concepto,
        monto: values.monto,
        metodo_pago: values.metodo_pago,
        tipo_referencia: 'ajuste',
      };

      const response = await apiClient.post('/gestion/caja-diaria', dataToSend);
      if (response.ok) {
        message.success('Movimiento registrado correctamente');
        setModalVisible(false);
        loadMovimientos();
        loadResumenHoy();
        loadResumenFormasPago();
        loadResumenGastos();
      } else {
        const error = await response.json();
        message.error(error.message || 'Error al registrar el movimiento');
      }
    } catch (error) {
      if (error.errorFields) return;
      console.error('Error:', error);
      message.error('Error al guardar el movimiento');
    }
  };

  const handleDelete = async (id) => {
    try {
      const response = await apiClient.delete(`/gestion/caja-diaria/${id}`);
      if (response.ok) {
        message.success('Movimiento eliminado correctamente');
        loadMovimientos();
        loadResumenHoy();
        loadResumenFormasPago();
        loadResumenGastos();
      } else {
        const error = await response.json();
        message.error(error.message || 'Error al eliminar el movimiento');
      }
    } catch (error) {
      console.error('Error:', error);
      message.error('Error al eliminar el movimiento');
    }
  };

  const handleVerDetalle = async (movimiento) => {
    const concepto = movimiento.caj_concepto || '';
    const matchTickets = concepto.match(/Pago (?:parcial de )?tickets ([0-9,\s]+)/i);
    if (matchTickets) {
      const ventaIds = matchTickets[1].split(',').map(s => parseInt(s.trim(), 10)).filter(id => !isNaN(id));
      if (ventaIds.length > 0) {
        setLoadingDetalle(true);
        setModalDetalleVisible(true);
        try {
          const results = await Promise.all(
            ventaIds.map(id =>
              Promise.all([
                apiClient.get(`/gestion/ventas/${id}`),
                apiClient.get(`/gestion/cobros/saldo/${id}`),
              ])
            )
          );
          const ventas = await Promise.all(results.map(async ([ventaRes, saldoRes]) => {
            const v = await ventaRes.json();
            const saldo = saldoRes.ok ? await saldoRes.json() : { total_pagado: 0, saldo_pendiente: 0 };
            return {
              ...v,
              total_pagado: parseFloat(saldo.total_pagado || 0),
              saldo_pendiente: parseFloat(saldo.saldo_pendiente || 0),
              detalles: (v.detalles || []).map(d => ({
                articulo_nombre: d.articulo_nombre,
                cantidad: d.ved_cantidad,
                subtotal: d.ved_subtotal,
              })),
            };
          }));
          setComprobanteSeleccionado({ tipo: 'cobro_multiples_ventas', ventas });
        } catch (err) {
          console.error(err);
          message.error('Error al cargar el detalle de las ventas');
          setModalDetalleVisible(false);
        } finally {
          setLoadingDetalle(false);
        }
        return;
      }
    }

    let ventaId = movimiento.caj_referencia_id;
    let tipoReferencia = movimiento.caj_tipo_referencia;

    // Si es un movimiento de cobro antiguo (tipo_referencia = 'ajuste' pero concepto contiene "Pago venta")
    // extraer el ID de la venta del concepto
    if (!ventaId && tipoReferencia === 'ajuste' && movimiento.caj_concepto) {
      const concepto = movimiento.caj_concepto;
      // Buscar patrón "Pago venta #X" o "Pago unificado ventas X, Y" y tomar el primer ID
      const matchVenta = concepto.match(/Pago venta #(\d+)/i) || concepto.match(/Pago unificado ventas ([\d,\s]+)/i);
      if (matchVenta) {
        if (matchVenta[1]) {
          // Si es "Pago unificado ventas X, Y", tomar el primer ID
          const ids = matchVenta[1].split(',').map(id => parseInt(id.trim())).filter(id => !isNaN(id));
          if (ids.length > 0) {
            ventaId = ids[0];
            tipoReferencia = 'venta';
          }
        } else if (matchVenta[0]) {
          // Si es "Pago venta #X"
          const idMatch = concepto.match(/#(\d+)/);
          if (idMatch) {
            ventaId = parseInt(idMatch[1]);
            tipoReferencia = 'venta';
          }
        }
      }
    }

    if (!ventaId || !tipoReferencia) {
      message.warning('Este movimiento no tiene comprobante asociado');
      return;
    }

    setLoadingDetalle(true);
    setModalDetalleVisible(true);
    
    try {
      let response;
      if (tipoReferencia === 'compra') {
        response = await apiClient.get(`/gestion/compras/${ventaId}`);
      } else if (tipoReferencia === 'venta') {
        response = await apiClient.get(`/gestion/ventas/${ventaId}`);
      } else if (tipoReferencia === 'gasto') {
        response = await apiClient.get(`/gestion/gastos/${ventaId}`);
      } else {
        message.warning('Tipo de comprobante no soportado');
        setLoadingDetalle(false);
        setModalDetalleVisible(false);
        return;
      }

      if (response.ok) {
        const data = await response.json();
        setComprobanteSeleccionado({
          ...data,
          tipo: tipoReferencia
        });
      } else {
        const error = await response.json();
        message.error(error.message || 'Error al cargar el detalle del comprobante');
        setModalDetalleVisible(false);
      }
    } catch (error) {
      console.error('Error:', error);
      message.error('Error al cargar el detalle del comprobante');
      setModalDetalleVisible(false);
    } finally {
      setLoadingDetalle(false);
    }
  };

  const handleFiltroFecha = (dates) => {
    setFechaRango(dates);
    if (dates && dates[0] && dates[1]) {
      loadMovimientos(dates);
      loadResumenHoy(dates);
      loadResumenFormasPago(dates);
      loadResumenGastos(dates);
    }
  };

  const columns = [
    {
      title: 'Fecha',
      dataIndex: 'caj_fecha_creacion',
      key: 'caj_fecha',
      render: (fechaCreacion, record) => fechaCreacion ? formatFechaConHora(fechaCreacion) : dayjs(record.caj_fecha).format('DD/MM/YYYY'),
    },
    {
      title: 'Tipo',
      dataIndex: 'caj_tipo_movimiento',
      key: 'caj_tipo_movimiento',
      render: (tipo) => (
        <Tag color={tipo === 'ingreso' ? 'green' : 'red'}>
          {tipo === 'ingreso' ? <ArrowUpOutlined /> : <ArrowDownOutlined />} {tipo.toUpperCase()}
        </Tag>
      ),
    },
    {
      title: 'Cuenta',
      dataIndex: 'cuenta_nombre',
      key: 'cuenta_nombre',
      render: (cuenta, record) => cuenta || record.caj_concepto || '-',
    },
    {
      title: 'Comprobante(s)',
      dataIndex: 'numero_comprobante',
      key: 'numero_comprobante',
      width: 180,
      render: (num, record) => num || '-',
    },
    {
      title: 'Forma de Pago',
      dataIndex: 'formas_pago_compra',
      key: 'formas_pago',
      width: 200,
      render: (formasPagoCompra, record) => {
        // Si es una compra con múltiples formas de pago, mostrar todas
        if (formasPagoCompra && Array.isArray(formasPagoCompra) && formasPagoCompra.length > 0) {
          return (
            <Space wrap size="small">
              {formasPagoCompra.map((fp, idx) => {
                const m = (fp.forma_pago || '').toLowerCase();
                const color = m === 'efectivo' ? 'green' : m === 'transferencia' ? 'blue' : m === 'qr' ? 'cyan' : m === 'tarjeta' ? 'purple' : 'orange';
                return (
                  <Tag key={idx} color={color}>
                    {fp.forma_pago} (${parseFloat(fp.monto).toFixed(2)})
                  </Tag>
                );
              })}
            </Space>
          );
        }
        // Si no hay formas de pago múltiples, mostrar la forma de pago del movimiento
        const metodo = record.caj_metodo_pago;
        if (!metodo) return '-';
        const m = (metodo || '').toLowerCase();
        const color = m === 'efectivo' ? 'green' : m === 'transferencia' ? 'blue' : m === 'qr' ? 'cyan' : m === 'tarjeta' ? 'purple' : 'orange';
        return (
          <Tag color={color}>
            {metodo}
          </Tag>
        );
      },
    },
    {
      title: 'Monto',
      dataIndex: 'total_comprobante',
      key: 'monto',
      render: (totalComprobante, record) => {
        // Si hay total_comprobante (compra), usar ese, sino usar caj_monto
        const monto = totalComprobante || record.caj_monto;
        return (
          <span style={{ color: record.caj_tipo_movimiento === 'ingreso' ? '#52c41a' : '#ff4d4f' }}>
            {record.caj_tipo_movimiento === 'ingreso' ? '+' : '-'}${parseFloat(monto).toFixed(2)}
          </span>
        );
      },
    },
    {
      title: 'Acciones',
      key: 'acciones',
      width: 150,
      render: (_, record) => {
        // Determinar si es un ajuste manual que se puede eliminar
        // Solo los ajustes que NO son de cobros ni cuenta corriente
        const esAjusteManual = record.caj_tipo_referencia === 'ajuste' && 
          (!record.caj_concepto || (
            !record.caj_concepto.toUpperCase().includes('PAGO VENTA') &&
            !record.caj_concepto.toUpperCase().includes('PAGO UNIFICADO VENTAS') &&
            !record.caj_concepto.toUpperCase().includes('PAGO CUENTA CORRIENTE') &&
            !record.caj_concepto.toUpperCase().includes('PAGO PARCIAL DE TICKETS') &&
            !record.caj_concepto.toUpperCase().includes('PAGO DE TICKETS')
          ));
        
        // Determinar si tiene comprobante para mostrar botón Ver
        // Incluye movimientos con referencia directa, cobros antiguos ("Pago venta"/"Pago unificado ventas")
        // y cobros de múltiples ventas ("Pago parcial de tickets" / "Pago de tickets")
        const esPagoTickets = record.caj_concepto && 
          (record.caj_concepto.includes('Pago parcial de tickets') || record.caj_concepto.includes('Pago de tickets'));
        const esMovimientoCobro = record.caj_tipo_referencia === 'ajuste' && 
          record.caj_concepto && 
          (record.caj_concepto.toUpperCase().includes('PAGO VENTA') || 
           record.caj_concepto.toUpperCase().includes('PAGO UNIFICADO VENTAS'));
        
        const tieneComprobante = (record.caj_referencia_id && 
          record.caj_tipo_referencia && 
          ['compra', 'venta', 'gasto'].includes(record.caj_tipo_referencia)) ||
          esMovimientoCobro ||
          esPagoTickets;
        
        return (
          <Space>
            {tieneComprobante && (
              <Button
                type="link"
                icon={<EyeOutlined />}
                onClick={() => handleVerDetalle(record)}
                size="small"
              >
                Ver
              </Button>
            )}
            {hasPermission('caja_diaria', 'eliminar') && esAjusteManual && (
              <Popconfirm
                title="¿Eliminar este ajuste?"
                onConfirm={() => handleDelete(record.caj_id)}
                okText="Sí"
                cancelText="No"
              >
                <Button type="link" danger icon={<DeleteOutlined />} size="small">
                  Eliminar
                </Button>
              </Popconfirm>
            )}
          </Space>
        );
      },
    },
  ];

  return (
    <MainLayout>
      <div style={{ padding: '24px' }}>
        <div style={{ marginBottom: 16 }}>
          <h1 style={{ margin: 0, marginBottom: 16, color: '#ffffff', fontSize: isMobile ? '20px' : '24px' }}>
            Caja Diaria
          </h1>
          <Space wrap style={{ marginBottom: 16 }}>
            <Button
              icon={<ReloadOutlined />}
              onClick={() => {
                loadMovimientos();
                loadResumenHoy();
                loadResumenFormasPago();
                loadResumenGastos();
                loadResumenCuentaCorriente();
              }}
              loading={loading}
            >
              {isMobile ? null : 'Actualizar'}
            </Button>
            {hasPermission('caja_diaria', 'crear') && (
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={handleOpenModal}
              >
                Nuevo Ajuste
              </Button>
            )}
          </Space>

          {/* Resumen del día */}
          <Card style={{ marginBottom: 16, backgroundColor: '#2d2d2d' }}>
            <Row gutter={16} wrap={isMobile} style={!isMobile ? { flexWrap: 'nowrap', overflowX: 'auto' } : undefined}>
              <Col xs={24} sm={12} md={6} lg={6} flex={isMobile ? undefined : '1 1 0'} style={!isMobile ? { minWidth: 140 } : undefined}>
                <Statistic
                  title={fechaRango && fechaRango[0] && fechaRango[1] && (fechaRango[0].format('DD/MM/YYYY') !== dayjs().format('DD/MM/YYYY') || fechaRango[1].format('DD/MM/YYYY') !== dayjs().format('DD/MM/YYYY'))
                    ? `Ingresos (${fechaRango[0].format('DD/MM')} - ${fechaRango[1].format('DD/MM')})`
                    : "Ingresos de Hoy"}
                  value={resumenHoy?.total_ingresos || 0}
                      prefix="$"
                      valueStyle={{ color: '#52c41a' }}
                    />
                    {/* Desglose por formas de pago - Ingresos */}
                    {resumenFormasPago && resumenFormasPago.length > 0 && resumenFormasPago.some(fp => parseFloat(fp.total_ingresos || 0) > 0) && (
                      <div style={{ marginTop: 12, fontSize: '14px' }}>
                        {resumenFormasPago
                          .filter(fp => parseFloat(fp.total_ingresos || 0) > 0)
                          .map((fp, index) => {
                            const monto = parseFloat(fp.total_ingresos || 0);
                            const partes = monto.toFixed(2).split('.');
                            partes[0] = partes[0].replace(/\B(?=(\d{3})+(?!\d))/g, '.');
                            const montoFormateado = partes.join(',');
                            return (
                              <div key={index} style={{ color: '#b0b0b0', marginTop: 4 }}>
                                {fp.forma_pago || 'Sin nombre'}: <span style={{ color: '#52c41a' }}>${montoFormateado}</span>
                              </div>
                            );
                          })}
                      </div>
                    )}
                  </Col>
                  <Col xs={24} sm={12} md={6} lg={6} flex={isMobile ? undefined : '1 1 0'} style={!isMobile ? { minWidth: 140 } : undefined}>
                    <Statistic
                      title={fechaRango && fechaRango[0] && fechaRango[1] && (fechaRango[0].format('DD/MM/YYYY') !== dayjs().format('DD/MM/YYYY') || fechaRango[1].format('DD/MM/YYYY') !== dayjs().format('DD/MM/YYYY'))
                        ? `Egresos (${fechaRango[0].format('DD/MM')} - ${fechaRango[1].format('DD/MM')})`
                        : "Egresos de Hoy"}
                      value={resumenHoy?.total_egresos || 0}
                      prefix="$"
                      valueStyle={{ color: '#ff4d4f' }}
                    />
                    {/* Desglose por formas de pago - Egresos */}
                    {resumenFormasPago && resumenFormasPago.length > 0 && resumenFormasPago.some(fp => parseFloat(fp.total_egresos || 0) > 0) && (
                      <div style={{ marginTop: 12, fontSize: '14px' }}>
                        {resumenFormasPago
                          .filter(fp => parseFloat(fp.total_egresos || 0) > 0)
                          .map((fp, index) => {
                            const monto = parseFloat(fp.total_egresos || 0);
                            const partes = monto.toFixed(2).split('.');
                            partes[0] = partes[0].replace(/\B(?=(\d{3})+(?!\d))/g, '.');
                            const montoFormateado = partes.join(',');
                            return (
                              <div key={index} style={{ color: '#b0b0b0', marginTop: 4 }}>
                                {fp.forma_pago || 'Sin nombre'}: <span style={{ color: '#ff4d4f' }}>${montoFormateado}</span>
                              </div>
                            );
                          })}
                      </div>
                    )}
                  </Col>
                  <Col xs={24} sm={12} md={6} lg={6} flex={isMobile ? undefined : '1 1 0'} style={!isMobile ? { minWidth: 140 } : undefined}>
                    <Statistic
                      title="Gastos"
                      value={resumenGastos.reduce((sum, g) => sum + (parseFloat(g.total_gastos) || 0), 0)}
                      prefix="$"
                      valueStyle={{ color: '#ff4d4f' }}
                    />
                    {/* Desglose por formas de pago - Gastos */}
                    {resumenGastos.length > 0 && (
                      <div style={{ marginTop: 12, fontSize: '14px' }}>
                        {resumenGastos.map((g, index) => {
                          const monto = parseFloat(g.total_gastos);
                          const partes = monto.toFixed(2).split('.');
                          partes[0] = partes[0].replace(/\B(?=(\d{3})+(?!\d))/g, '.');
                          const montoFormateado = partes.join(',');
                          return (
                            <div key={index} style={{ color: '#b0b0b0', marginTop: 4 }}>
                              {g.forma_pago}: <span style={{ color: '#ff4d4f' }}>${montoFormateado}</span>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </Col>
                  <Col xs={24} sm={12} md={6} lg={6} flex={isMobile ? undefined : '1 1 0'} style={!isMobile ? { minWidth: 140 } : undefined}>
                    <Statistic
                      title={fechaRango && fechaRango[0] && fechaRango[1] && (fechaRango[0].format('DD/MM/YYYY') !== dayjs().format('DD/MM/YYYY') || fechaRango[1].format('DD/MM/YYYY') !== dayjs().format('DD/MM/YYYY'))
                        ? `Ctacte (${fechaRango[0].format('DD/MM')} - ${fechaRango[1].format('DD/MM')})`
                        : "Cuenta Corriente"}
                      value={resumenCuentaCorriente.total_deuda || 0}
                      prefix="$"
                      valueStyle={{ color: '#722ed1' }}
                    />
                  </Col>
                  <Col xs={24} sm={12} md={6} lg={6} flex={isMobile ? undefined : '1 1 0'} style={!isMobile ? { minWidth: 140 } : undefined}>
                    <Statistic
                      title={fechaRango && fechaRango[0] && fechaRango[1] && (fechaRango[0].format('DD/MM/YYYY') !== dayjs().format('DD/MM/YYYY') || fechaRango[1].format('DD/MM/YYYY') !== dayjs().format('DD/MM/YYYY'))
                        ? `Saldo (${fechaRango[0].format('DD/MM')} - ${fechaRango[1].format('DD/MM')})`
                        : "Saldo Diario"}
                      value={resumenHoy?.saldo_diario || 0}
                      prefix="$"
                      valueStyle={{
                        color: (resumenHoy?.saldo_diario || 0) >= 0 ? '#52c41a' : '#ff4d4f',
                      }}
                    />
                    {/* Saldo en efectivo para rendir caja */}
                    {(() => {
                      const efectivoRows = (resumenFormasPago || []).filter(
                        fp => fp.forma_pago && String(fp.forma_pago).toLowerCase() === 'efectivo'
                      );
                      const efectivoGastos = (resumenGastos || []).filter(
                        g => g.forma_pago && String(g.forma_pago).toLowerCase() === 'efectivo'
                      );
                      const saldoEfectivo = efectivoRows.reduce(
                        (sum, fp) => sum + (parseFloat(fp.total_ingresos) || 0) - (parseFloat(fp.total_egresos) || 0),
                        0
                      ) - efectivoGastos.reduce((sum, g) => sum + (parseFloat(g.total_gastos) || 0), 0);
                      const partes = saldoEfectivo.toFixed(2).split('.');
                      partes[0] = partes[0].replace(/\B(?=(\d{3})+(?!\d))/g, '.');
                      const montoFormateado = partes.join(',');
                      return (
                        <div style={{ marginTop: 12, fontSize: '14px', color: '#b0b0b0' }}>
                          Efectivo: <span style={{ color: saldoEfectivo >= 0 ? '#52c41a' : '#ff4d4f', fontWeight: 500 }}>${montoFormateado}</span>
                        </div>
                      );
                    })()}
                  </Col>
                </Row>
              </Card>

          {/* Filtro de fechas */}
          <div style={{ marginBottom: 16 }}>
            <RangePicker
              value={fechaRango}
              onChange={handleFiltroFecha}
              format="DD/MM/YYYY"
              style={{ width: isMobile ? '100%' : 300 }}
            />
          </div>
        </div>

        <Table
          columns={columns}
          dataSource={movimientos}
          loading={loading}
          rowKey="caj_id"
          pagination={{
            pageSize: 20,
            showSizeChanger: true,
            showTotal: (total) => `Total: ${total} movimientos`,
          }}
        />

        {/* Modal para crear ajuste manual */}
        <Modal
          title="Nuevo Ajuste Manual"
          open={modalVisible}
          onOk={handleSave}
          onCancel={() => {
            setModalVisible(false);
            form.resetFields();
          }}
          okText="Registrar"
          cancelText="Cancelar"
          width={500}
        >
          <Form form={form} layout="vertical">
            <Form.Item
              name="fecha"
              label="Fecha"
              rules={[{ required: true, message: 'Seleccione la fecha' }]}
            >
              <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" />
            </Form.Item>

            <Form.Item
              name="tipo_movimiento"
              label="Tipo de Movimiento"
              rules={[{ required: true, message: 'Seleccione el tipo' }]}
            >
              <Select>
                <Select.Option value="ingreso">Ingreso</Select.Option>
                <Select.Option value="egreso">Egreso</Select.Option>
              </Select>
            </Form.Item>

            <Form.Item
              name="concepto"
              label="Concepto"
              rules={[{ required: true, message: 'Ingrese el concepto' }]}
            >
              <TextArea rows={2} placeholder="Descripción del movimiento" />
            </Form.Item>

            <Form.Item
              name="monto"
              label="Monto"
              rules={[{ required: true, message: 'Ingrese el monto' }]}
            >
              <InputNumber
                style={{ width: '100%' }}
                min={0.01}
                step={0.01}
                formatter={value => `$ ${value}`}
                parser={value => value.replace(/\$\s?|(,*)/g, '')}
                placeholder="0.00"
              />
            </Form.Item>

            <Form.Item
              name="metodo_pago"
              label="Método de Pago"
              rules={[{ required: true, message: 'Seleccione el método' }]}
            >
              <Select>
                <Select.Option value="efectivo">Efectivo</Select.Option>
                <Select.Option value="transferencia">Transferencia</Select.Option>
                <Select.Option value="tarjeta">Tarjeta</Select.Option>
                <Select.Option value="mixto">Mixto</Select.Option>
              </Select>
            </Form.Item>
          </Form>
        </Modal>

        {/* Modal para ver detalle del comprobante */}
        <Modal
          title={
            comprobanteSeleccionado?.tipo === 'compra' ? 'Detalle de Compra' :
            comprobanteSeleccionado?.tipo === 'venta' ? 'Detalle de Venta' :
            comprobanteSeleccionado?.tipo === 'gasto' ? 'Detalle de Gasto' :
            comprobanteSeleccionado?.tipo === 'cobro_multiples_ventas' ? `Ventas #${(comprobanteSeleccionado.ventas || []).map(v => v.ven_id).join(', #')}` :
            'Detalle del Comprobante'
          }
          open={modalDetalleVisible}
          onCancel={() => {
            setModalDetalleVisible(false);
            setComprobanteSeleccionado(null);
          }}
          footer={null}
          width={600}
          loading={loadingDetalle}
        >
          {comprobanteSeleccionado && comprobanteSeleccionado.tipo === 'compra' && (
            <>
              <Descriptions column={1} bordered>
                <Descriptions.Item label="Fecha">
                  {dayjs(comprobanteSeleccionado.com_fecha).format('DD/MM/YYYY')}
                </Descriptions.Item>
                <Descriptions.Item label="Proveedor">
                  {comprobanteSeleccionado.proveedor_nombre}
                </Descriptions.Item>
                <Descriptions.Item label="Total">
                  {comprobanteSeleccionado.com_total_comprobante != null 
                    ? `$${parseFloat(comprobanteSeleccionado.com_total_comprobante).toFixed(2)}`
                    : comprobanteSeleccionado.com_total != null
                    ? `$${parseFloat(comprobanteSeleccionado.com_total).toFixed(2)}`
                    : '-'
                  }
                </Descriptions.Item>
                {comprobanteSeleccionado.com_observaciones && (
                  <Descriptions.Item label="Observaciones">
                    {comprobanteSeleccionado.com_observaciones}
                  </Descriptions.Item>
                )}
              </Descriptions>
              
              {/* Formas de Pago */}
              {comprobanteSeleccionado.formas_pago && comprobanteSeleccionado.formas_pago.length > 0 && (
                <>
                  <Divider>Formas de Pago</Divider>
                  <Table
                    columns={[
                      { title: 'Forma de Pago', dataIndex: 'forma_pago_nombre', key: 'forma_pago_nombre' },
                      { 
                        title: 'Monto', 
                        dataIndex: 'cfp_monto', 
                        key: 'cfp_monto',
                        align: 'right',
                        render: (monto) => `$${parseFloat(monto).toFixed(2)}`
                      },
                    ]}
                    dataSource={comprobanteSeleccionado.formas_pago}
                    pagination={false}
                    size="small"
                    summary={() => (
                      <Table.Summary fixed>
                        <Table.Summary.Row>
                          <Table.Summary.Cell index={0} align="right">
                            <strong>Total:</strong>
                          </Table.Summary.Cell>
                          <Table.Summary.Cell index={1} align="right">
                            <strong>
                              ${comprobanteSeleccionado.formas_pago.reduce((sum, fp) => sum + (parseFloat(fp.cfp_monto) || 0), 0).toFixed(2)}
                            </strong>
                          </Table.Summary.Cell>
                        </Table.Summary.Row>
                      </Table.Summary>
                    )}
                  />
                </>
              )}
              
              <Divider>Artículos</Divider>
              <Table
                columns={[
                  { title: 'Artículo', dataIndex: 'articulo_nombre', key: 'articulo_nombre' },
                  { title: 'Cantidad', dataIndex: 'cod_cantidad', key: 'cod_cantidad', align: 'center' },
                  {
                    title: 'Precio Unit.',
                    dataIndex: 'cod_precio_unitario',
                    key: 'cod_precio_unitario',
                    align: 'right',
                    render: (precio) => precio ? `$${parseFloat(precio).toFixed(2)}` : '-',
                  },
                  {
                    title: 'Subtotal',
                    dataIndex: 'cod_subtotal',
                    key: 'cod_subtotal',
                    align: 'right',
                    render: (subtotal) => subtotal ? `$${parseFloat(subtotal).toFixed(2)}` : '-',
                  },
                ]}
                dataSource={comprobanteSeleccionado.detalles || []}
                pagination={false}
                size="small"
              />
            </>
          )}

          {comprobanteSeleccionado && comprobanteSeleccionado.tipo === 'venta' && (
            <>
              <Descriptions column={1} bordered>
                <Descriptions.Item label="Fecha">
                  {comprobanteSeleccionado.ven_fecha_creacion ? formatFechaConHora(comprobanteSeleccionado.ven_fecha_creacion) : dayjs(comprobanteSeleccionado.ven_fecha).format('DD/MM/YYYY')}
                </Descriptions.Item>
                <Descriptions.Item label="Método de Pago">
                  <Tag color={(comprobanteSeleccionado.ven_metodo_pago || '').toLowerCase() === 'efectivo' ? 'green' : (comprobanteSeleccionado.ven_metodo_pago || '').toLowerCase() === 'qr' ? 'cyan' : 'blue'}>
                    {comprobanteSeleccionado.ven_metodo_pago}
                  </Tag>
                </Descriptions.Item>
                <Descriptions.Item label="Total">
                  ${parseFloat(comprobanteSeleccionado.ven_total).toFixed(2)}
                  {comprobanteSeleccionado.ven_descuento_porcentaje != null && Number(comprobanteSeleccionado.ven_descuento_porcentaje) > 0 && (
                    <span style={{ color: '#888', marginLeft: 8 }}>(con {Number(comprobanteSeleccionado.ven_descuento_porcentaje)}% dto.)</span>
                  )}
                </Descriptions.Item>
                <Descriptions.Item label="Destino">
                  {(comprobanteSeleccionado.ven_tipo_destino || 'mostrador') === 'cancha' && (
                    <Tag color="blue">Cancha: {comprobanteSeleccionado.cancha_nombre || '-'}</Tag>
                  )}
                  {(comprobanteSeleccionado.ven_tipo_destino || 'mostrador') === 'mesa' && (
                    <Tag color="purple">Mesa: {comprobanteSeleccionado.mesa_nombre || '-'}</Tag>
                  )}
                  {(comprobanteSeleccionado.ven_tipo_destino || 'mostrador') === 'mostrador' && (
                    <Tag>Mostrador</Tag>
                  )}
                </Descriptions.Item>
                {comprobanteSeleccionado.ven_observaciones && (
                  <Descriptions.Item label="Observaciones">
                    {comprobanteSeleccionado.ven_observaciones}
                  </Descriptions.Item>
                )}
              </Descriptions>
              <Divider>Artículos</Divider>
              <Table
                columns={[
                  { title: 'Artículo', dataIndex: 'articulo_nombre', key: 'articulo_nombre' },
                  { title: 'Cantidad', dataIndex: 'ved_cantidad', key: 'ved_cantidad' },
                  {
                    title: 'Precio Unit.',
                    dataIndex: 'ved_precio_unitario',
                    key: 'ved_precio_unitario',
                    render: (precio) => `$${parseFloat(precio).toFixed(2)}`,
                  },
                  {
                    title: 'Subtotal',
                    dataIndex: 'ved_subtotal',
                    key: 'ved_subtotal',
                    render: (subtotal) => `$${parseFloat(subtotal).toFixed(2)}`,
                  },
                ]}
                dataSource={comprobanteSeleccionado.detalles || []}
                pagination={false}
                size="small"
              />
            </>
          )}

          {comprobanteSeleccionado && comprobanteSeleccionado.tipo === 'gasto' && (
            <>
              <Descriptions column={1} bordered>
                <Descriptions.Item label="Fecha">
                  {dayjs(comprobanteSeleccionado.gas_fecha).format('DD/MM/YYYY')}
                </Descriptions.Item>
                <Descriptions.Item label="Método de Pago">
                  <Tag color={comprobanteSeleccionado.gas_metodo_pago === 'efectivo' ? 'green' : 'blue'}>
                    {comprobanteSeleccionado.gas_metodo_pago}
                  </Tag>
                </Descriptions.Item>
                <Descriptions.Item label="Total">
                  ${parseFloat(comprobanteSeleccionado.gas_total).toFixed(2)}
                </Descriptions.Item>
                {comprobanteSeleccionado.gas_observaciones && (
                  <Descriptions.Item label="Observaciones">
                    {comprobanteSeleccionado.gas_observaciones}
                  </Descriptions.Item>
                )}
              </Descriptions>
              <Divider>Artículos</Divider>
              <Table
                columns={[
                  { title: 'Artículo', dataIndex: 'articulo_nombre', key: 'articulo_nombre' },
                  {
                    title: 'Importe',
                    dataIndex: 'gad_subtotal',
                    key: 'gad_subtotal',
                    align: 'right',
                    render: (subtotal) => `$${parseFloat(subtotal).toFixed(2)}`,
                  },
                ]}
                dataSource={comprobanteSeleccionado.detalles || []}
                pagination={false}
                size="small"
              />
            </>
          )}

          {comprobanteSeleccionado && comprobanteSeleccionado.tipo === 'cobro_multiples_ventas' && comprobanteSeleccionado.ventas && comprobanteSeleccionado.ventas.length > 0 && (
            <Card
              size="small"
              style={{ backgroundColor: '#1a1a1a', border: '2px solid #434343' }}
              title={
                <span style={{ color: '#ffffff', fontWeight: 'bold' }}>
                  Ventas {comprobanteSeleccionado.ventas.map(v => `#${v.ven_id}`).join(', ')}
                </span>
              }
            >
              <Space direction="vertical" style={{ width: '100%' }} size="middle">
                {comprobanteSeleccionado.ventas.map((venta) => (
                  <div key={venta.ven_id}>
                    <span style={{ color: '#1890ff', fontWeight: 600, display: 'block', marginBottom: 6 }}>
                      Consumo Venta #{venta.ven_id} ({venta.ven_fecha_creacion ? formatFechaConHora(venta.ven_fecha_creacion) : dayjs(venta.ven_fecha).format('DD/MM/YYYY')})
                    </span>
                    {venta.detalles && venta.detalles.length > 0 ? (
                      <div style={{ marginLeft: 12, marginBottom: 8 }}>
                        {venta.detalles.map((det, idx) => (
                          <div key={idx} style={{ color: '#b0b0b0' }}>
                            • {det.articulo_nombre} x{det.cantidad} - ${parseFloat(det.subtotal).toFixed(2)}
                          </div>
                        ))}
                      </div>
                    ) : (
                      <span style={{ color: '#b0b0b0', marginLeft: 12 }}>-</span>
                    )}
                  </div>
                ))}
                <Divider style={{ borderColor: '#434343' }} />
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ color: '#b0b0b0' }}>Total:</span>
                  <span style={{ color: '#ffffff', fontWeight: 600 }}>
                    ${comprobanteSeleccionado.ventas.reduce((s, v) => s + parseFloat(v.ven_total || 0), 0).toFixed(2)}
                  </span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ color: '#b0b0b0' }}>Pagado:</span>
                  <span style={{ color: '#52c41a' }}>
                    ${comprobanteSeleccionado.ventas.reduce((s, v) => s + parseFloat(v.total_pagado || 0), 0).toFixed(2)}
                  </span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ color: '#b0b0b0' }}>Pendiente:</span>
                  <span style={{ color: '#ff4d4f', fontWeight: 600, fontSize: 16 }}>
                    ${comprobanteSeleccionado.ventas.reduce((s, v) => s + parseFloat(v.saldo_pendiente || 0), 0).toFixed(2)}
                  </span>
                </div>
              </Space>
            </Card>
          )}
        </Modal>
      </div>
    </MainLayout>
  );
};

export default CajaDiaria;
